import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios"
import { useAuthStore } from "@/stores/auth"
import { notifyError } from "@/utils/notify"

// CSRF token storage
let csrfToken: string | null = localStorage.getItem("csrf_token")

const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "http://localhost:8080/api/v1",
  timeout: 30000,
  headers: {
    Accept: "application/json",
  },
  withCredentials: true, // Send httpOnly cookies (session)
})

// Fetch CSRF token from backend
export const fetchCsrfToken = async (): Promise<string | null> => {
  try {
    const response = await apiClient.get<{ csrf_token: string }>("/csrf-token")
    if (response.data.csrf_token) {
      csrfToken = response.data.csrf_token
      localStorage.setItem("csrf_token", csrfToken)
    }
    return csrfToken
  } catch (_error) {
    return null
  }
}

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const authStore = useAuthStore()
    const token = authStore.token || localStorage.getItem("token")

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Set Content-Type based on data type
    if (config.data instanceof FormData) {
      // Browser will automatically set multipart/form-data with correct boundary
      delete config.headers["Content-Type"]
    } else {
      config.headers["Content-Type"] = "application/json"
    }

    // Attach CSRF token for state-changing requests
    const method = config.method?.toLowerCase() || ""
    if (["post", "put", "delete", "patch"].includes(method)) {
      if (!csrfToken) {
        // Auto-fetch CSRF token if not available
        await fetchCsrfToken()
      }
      if (csrfToken) {
        config.headers["X-CSRF-Token"] = csrfToken
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response) {
      const { status, data } = error.response

      // Retry logic for CSRF token expiration (403)
      if (status === 403 && data?.error === "csrf_token_invalid" && !originalRequest._retry) {
        originalRequest._retry = true
        await fetchCsrfToken()
        return apiClient(originalRequest)
      }

      switch (status) {
        case 400:
          notifyError(data?.message || "Bad Request")
          break
        case 401:
          if (!originalRequest._retry) {
            originalRequest._retry = true
            const authStore = useAuthStore()
            authStore.logout()
            // Save current page for redirect after login
            const currentPath = window.location.pathname + window.location.search
            const isAdminLoginPath =
              currentPath === "/admin" ||
              currentPath === "/admin/" ||
              currentPath.startsWith("/admin?")
            if (!isAdminLoginPath) {
              window.location.href = `/admin?redirect=${encodeURIComponent(currentPath)}`
            } else {
              window.location.href = "/admin"
            }
          }
          break
        case 403:
          // login_required: biarkan komponen menampilkan pesan (bukan izin umum)
          if (data?.error !== "login_required") {
            notifyError(data?.message || "You do not have permission to perform this action")
          }
          break
        case 404:
          notifyError(data?.message || "Resource not found")
          break
        case 422:
          notifyError(data?.message || "Validation Error")
          break
        case 500:
          notifyError(data?.message || "Internal server error. Please try again later.")
          break
        default:
          notifyError(data?.message || "An unexpected error occurred")
      }
    } else if (error.request) {
      notifyError("Network error. Please check your connection.")
    } else {
      notifyError("An unexpected error occurred")
    }

    return Promise.reject(error)
  },
)

// biome-ignore lint/suspicious/noExplicitAny: Generic API helpers - type safety provided by generic parameter T
export const get = async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.get<T>(url, config)
  return response.data
}

// biome-ignore lint/suspicious/noExplicitAny: Generic API helpers - type safety provided by generic parameter T
export const post = async <T = any>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const response = await apiClient.post<T>(url, data, config)
  return response.data
}

// biome-ignore lint/suspicious/noExplicitAny: Generic API helpers - type safety provided by generic parameter T
export const put = async <T = any>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const response = await apiClient.put<T>(url, data, config)
  return response.data
}

// biome-ignore lint/suspicious/noExplicitAny: Generic API helpers - type safety provided by generic parameter T
export const patch = async <T = any>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> => {
  const response = await apiClient.patch<T>(url, data, config)
  return response.data
}

// biome-ignore lint/suspicious/noExplicitAny: Generic API helpers - type safety provided by generic parameter T
export const del = async <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response = await apiClient.delete<T>(url, config)
  return response.data
}

// biome-ignore lint/suspicious/noExplicitAny: Progress event type from axios
export const upload = async <T = any>(
  url: string,
  formData: FormData,
  onProgress?: (progressEvent: unknown) => void,
): Promise<T> => {
  // DO NOT set Content-Type manually!
  // Browser will automatically set multipart/form-data with correct boundary
  const response = await apiClient.post<T>(url, formData, {
    onUploadProgress: onProgress,
  })
  return response.data
}

// biome-ignore lint/suspicious/noExplicitAny: Generic API helpers - type safety provided by generic parameter T
export const uploadPut = async <T = any>(url: string, formData: FormData): Promise<T> => {
  const response = await apiClient.put<T>(url, formData)
  return response.data
}

export default apiClient
